import { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { EyeIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { apiClient } from '../services/api'
import type { Template } from '../types'
import { sanitizeHtml, stripHtmlTags } from '../lib/sanitizeHtml'
import { LikeButton } from './LikeButton'

interface TemplateListProps {
  selectedTemplates: number[]
  setSelectedTemplates: (templates: number[]) => void
}

const SORT_PREFERENCE_KEY = 'emailCatalogSortBy'
const INFORNER_DISMISSED_KEY = 'emailCatalogInfomerDismissed'

// Load saved sort preference or default to 'random'
const loadSortPreference = (): 'random' | 'date' | 'popularity' | 'views' | 'likes' | 'category' | 'resources' => {
  const saved = localStorage.getItem(SORT_PREFERENCE_KEY)
  if (saved && ['random', 'date', 'popularity', 'views', 'likes', 'category', 'resources'].includes(saved)) {
    return saved as 'random' | 'date' | 'popularity' | 'views' | 'likes' | 'category' | 'resources'
  }
  return 'random' // Default to random
}

// Check if infomer was dismissed
const isInfomerDismissed = (): boolean => {
  return localStorage.getItem(INFORNER_DISMISSED_KEY) === 'true'
}

// Check if template is new (added this week)
const isNewTemplate = (createdAt: string): boolean => {
  const now = new Date()
  const created = new Date(createdAt)
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  return created >= weekAgo
}

export default function TemplateList({ selectedTemplates, setSelectedTemplates }: TemplateListProps) {
  const [filters, setFilters] = useState({
    category: '',
    search: '',
    resource_type: ''
  })
  const [searchInput, setSearchInput] = useState('') // Local search input for debouncing
  const [hoveredTemplate, setHoveredTemplate] = useState<number | null>(null)
  const [sortBy, setSortBy] = useState<'random' | 'date' | 'popularity' | 'views' | 'likes' | 'category' | 'resources'>(loadSortPreference())
  const [showInfomer, setShowInfomer] = useState(!isInfomerDismissed())

  // Save sort preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(SORT_PREFERENCE_KEY, sortBy)
  }, [sortBy])

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }))
    }, 300) // 300ms delay

    return () => clearTimeout(timeoutId)
  }, [searchInput])

  // Separate query for templates without search to avoid full page re-render
  const { data: rawTemplates = [], isLoading } = useQuery({
    queryKey: ['templates', { category: filters.category, resource_type: filters.resource_type }],
    queryFn: () => apiClient.getTemplates({ 
      category: filters.category, 
      resource_type: filters.resource_type 
    })
  })

  // Filter templates locally for search to avoid API calls
  const filteredTemplates = useMemo(() => {
    return rawTemplates.filter(template => {
      if (!filters.search) return true
      const searchLower = filters.search.toLowerCase()
      const descPlain = template.description
        ? stripHtmlTags(template.description).toLowerCase()
        : ''
      return (
        template.name.toLowerCase().includes(searchLower) ||
        descPlain.includes(searchLower)
      )
    })
  }, [rawTemplates, filters.search])

  // Sort templates based on selected criteria
  const templates = useMemo(() => {
    const templatesCopy = [...filteredTemplates]
    
    if (sortBy === 'random') {
      // Fisher-Yates shuffle for random order
      for (let i = templatesCopy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [templatesCopy[i], templatesCopy[j]] = [templatesCopy[j], templatesCopy[i]]
      }
      return templatesCopy
    }
    
    return templatesCopy.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          const dateA = new Date(a.created_at).getTime()
          const dateB = new Date(b.created_at).getTime()
          // If dates are equal, sort by ID (newer IDs first)
          if (dateA === dateB) {
            return b.id - a.id
          }
          return dateB - dateA
        case 'popularity':
          return b.popularity_score - a.popularity_score
        case 'views':
          return b.view_count - a.view_count
        case 'likes':
          return b.like_count - a.like_count
        case 'category':
          return (a.category || '').localeCompare(b.category || '')
        case 'resources':
          return b.resource_count - a.resource_count
        default:
          return 0
      }
    })
  }, [filteredTemplates, sortBy])

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiClient.getCategories()
  })

  const { data: resourceTypes = [] } = useQuery({
    queryKey: ['resourceTypes'],
    queryFn: () => apiClient.getResourceTypes()
  })

  const toggleTemplateSelection = (templateId: number) => {
    if (selectedTemplates.includes(templateId)) {
      setSelectedTemplates(selectedTemplates.filter(id => id !== templateId))
    } else {
      setSelectedTemplates([...selectedTemplates, templateId])
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const handleSelectAll = () => {
    setSelectedTemplates(templates.map((t: Template) => t.id))
  }

  const handleDeselectAll = () => {
    setSelectedTemplates([])
  }

  const handleDismissInfomer = () => {
    setShowInfomer(false)
    localStorage.setItem(INFORNER_DISMISSED_KEY, 'true')
  }

  return (
    <div className="space-y-6">
      {/* Infomer */}
      {showInfomer && (
        <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200 rounded-lg p-6 relative">
          <button
            onClick={handleDismissInfomer}
            className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Dismiss infomer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <div className="pr-8">
            <p className="text-gray-800 text-lg leading-relaxed">
              <strong>Explore email templates</strong>, send them to your inbox, and see how they look live. 
              Like your favorites — we'll keep growing the library. 
              <strong>Let's bring more fun into email together!</strong> 🚀
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm lg:sticky lg:top-4 lg:z-10">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Filters & Sorting</h2>
          
          {/* Selection Counter & Actions */}
          {templates.length > 0 && (
            <div className="flex items-center space-x-3">
              {selectedTemplates.length > 0 && (
                <span className="text-sm font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                  {selectedTemplates.length} selected
                </span>
              )}
              <div className="flex items-center space-x-2 text-sm">
                <button
                  onClick={handleSelectAll}
                  className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  Select All
                </button>
                <span className="text-gray-400">|</span>
                <button
                  onClick={handleDeselectAll}
                  className="text-gray-600 hover:text-gray-700 font-medium transition-colors"
                >
                  Deselect All
                </button>
              </div>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Category
            </label>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Resource Type
            </label>
            <select
              value={filters.resource_type}
              onChange={(e) => setFilters({ ...filters, resource_type: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Types</option>
              {resourceTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
              Search
            </label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name or description..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Sorting - compact dropdown */}
        <div className="mt-4 flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">
            Sort by:
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="random">🎲 Random</option>
            <option value="date">📅 Date Created</option>
            <option value="popularity">⭐ Popularity</option>
            <option value="views">👁️ Views</option>
            <option value="likes">❤️ Likes</option>
            <option value="category">📁 Category</option>
            <option value="resources">📚 Resources</option>
          </select>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        {templates.map((template: Template) => (
          <div
            key={template.id}
            className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow flex flex-col h-full"
          >
            {/* Template Preview */}
            <Link 
              to={`/template/${template.id}`} 
              className="relative block flex-shrink-0"
              onMouseEnter={() => template.gif_url && setHoveredTemplate(template.id)}
              onMouseLeave={() => template.gif_url && setHoveredTemplate(null)}
            >
              {template.preview_image ? (
                <img
                  src={hoveredTemplate === template.id && template.gif_url ? template.gif_url : template.preview_image}
                  alt={template.name}
                  className="w-full h-48 object-cover cursor-pointer"
                />
              ) : (
                <div className="w-full h-48 bg-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-300 transition-colors">
                  <span className="text-gray-500">No Image</span>
                </div>
              )}
              
              {/* Selection Checkbox */}
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  toggleTemplateSelection(template.id)
                }}
                className={`absolute top-2 right-2 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                  selectedTemplates.includes(template.id)
                    ? 'bg-primary-500 border-primary-500 text-white'
                    : 'bg-white border-gray-300 text-gray-400 hover:border-primary-500'
                }`}
              >
                {selectedTemplates.includes(template.id) && (
                  <CheckIcon className="w-5 h-5" />
                )}
              </button>
              
              {/* Category Badge */}
              {template.category && (
                <span className="absolute top-2 left-2 bg-primary-500 text-white px-2 py-1 rounded text-xs font-medium">
                  {template.category}
                </span>
              )}
              
              {/* New Badge */}
              {isNewTemplate(template.created_at) && (
                <span className="absolute top-10 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-bold shadow-lg">
                  NEW
                </span>
              )}
            </Link>

            {/* Template Info */}
            <div className="p-4 flex flex-col flex-1">
              <h3 className="font-semibold text-lg mb-2">{template.name}</h3>
              
              {/* Fixed height description area for 2 lines (HTML allowed, sanitized) */}
              <div className="text-gray-600 text-sm mb-3 h-10 flex items-start">
                {template.description?.trim() ? (
                  <div
                    className="line-clamp-2 leading-5 w-full break-words [&_a]:text-primary-500 [&_a]:underline"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(template.description),
                    }}
                  />
                ) : (
                  <p className="line-clamp-2 leading-5 text-gray-400">No description available</p>
                )}
              </div>

              {/* Fallback Stack */}
              {template.features && template.features.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {template.features.slice(0, 3).map((feature) => (
                    <span
                      key={feature}
                      className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs"
                    >
                      {feature}
                    </span>
                  ))}
                  {template.features.length > 3 && (
                    <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                      +{template.features.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Stats */}
              <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                <span>Views: {template.view_count}</span>
                <span>Popularity: {template.popularity_score}</span>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between mt-auto pt-4">
                <LikeButton 
                  templateId={template.id} 
                  initialLikeCount={template.like_count}
                  size="sm"
                />
                <Link
                  to={`/template/${template.id}`}
                  className="flex items-center space-x-1 text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <EyeIcon className="w-4 h-4" />
                  <span>Details</span>
                </Link>
                
                {template.resource_count > 0 && (
                  <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {template.resource_count} resource{template.resource_count !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {templates.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No emails found</p>
          <p className="text-gray-400">Try adjusting your search filters</p>
        </div>
      )}
    </div>
  )
}
